/**
 * Version history and scenarios for a v2 model.
 *
 * Restore asks for confirmation. It is undoable — the server snapshots the
 * current state first — but a silent jump backwards in a spreadsheet is
 * alarming enough to be worth one click, and the confirmation is where the
 * user learns that the current state is kept.
 *
 * Scenarios and plain versions come from one list on the server, separated by
 * whether they carry a name. They are shown apart because they mean different
 * things: a scenario is something the user chose to keep, an unnamed version
 * is undo history.
 */

import { useEffect, useState } from 'react';
import { useFinancialModelV2Store } from '../../store/financial-model-v2';
import type { ModelVersionMetaV2 } from '../../types/financial-model-v2';

function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function VersionRow({
  entry,
  isCurrent,
  onRestore,
  onExport,
  onDelete,
}: {
  entry: ModelVersionMetaV2;
  isCurrent: boolean;
  onRestore: () => void;
  onExport: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <div className="min-w-0">
        <div className="truncate text-sm text-gray-900">
          {entry.scenarioName ?? `Version ${entry.version}`}
          {isCurrent && (
            <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-blue-700">
              current
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500">
          v{entry.version} · {when(entry.createdAt)}
        </div>
      </div>
      <div className="flex shrink-0 gap-1">
        <button
          type="button"
          onClick={onExport}
          className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
        >
          Export
        </button>
        <button
          type="button"
          disabled={isCurrent}
          onClick={onRestore}
          className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          Restore
        </button>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="rounded border border-gray-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

export function VersionsViewV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const versions = useFinancialModelV2Store((s) => s.versions);
  const scenarios = useFinancialModelV2Store((s) => s.scenarios);
  const listVersions = useFinancialModelV2Store((s) => s.listVersions);
  const restoreVersion = useFinancialModelV2Store((s) => s.restoreVersion);
  const saveScenario = useFinancialModelV2Store((s) => s.saveScenario);
  const deleteScenario = useFinancialModelV2Store((s) => s.deleteScenario);
  const exportVersion = useFinancialModelV2Store((s) => s.exportVersion);
  const clearAllOverrides = useFinancialModelV2Store((s) => s.clearAllOverrides);
  const resetInputs = useFinancialModelV2Store((s) => s.resetInputs);

  const [scenarioName, setScenarioName] = useState('');
  const [pendingRestore, setPendingRestore] = useState<number | null>(null);

  useEffect(() => {
    void listVersions();
  }, [listVersions, model?.version]);

  if (!model) return null;

  const overrideCount =
    Object.values(model.overrides ?? {}).reduce(
      (total, bucket) =>
        total +
        Object.values(bucket as Record<string, Record<string, number>>).reduce(
          (fieldTotal, periods) => fieldTotal + Object.keys(periods).length,
          0,
        ),
      0,
    ) ?? 0;

  // Unnamed versions only: the named ones are listed as scenarios above, and
  // showing them twice makes the history look longer than it is.
  const history = versions.filter((v) => !v.scenarioName);

  return (
    <div className="h-full overflow-auto p-6">
      <h2 className="mb-4 text-lg font-bold text-gray-800">Versions & scenarios</h2>

      <div className="max-w-3xl space-y-6">
        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-700">Save a scenario</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Keeps the model exactly as it stands now, under a name you choose.
            </p>
          </div>
          <div className="flex gap-2 px-4 py-3">
            <input
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              placeholder="e.g. Upside case"
              className="flex-1 rounded border border-gray-300 px-3 py-1.5 text-sm"
            />
            <button
              type="button"
              disabled={!scenarioName.trim()}
              onClick={async () => {
                await saveScenario(scenarioName.trim());
                setScenarioName('');
              }}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
            >
              Save
            </button>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">
            Scenarios
          </div>
          {scenarios.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              No saved scenarios yet.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {scenarios.map((entry) => (
                <VersionRow
                  key={entry.version}
                  entry={entry}
                  isCurrent={entry.version === model.version}
                  onRestore={() => setPendingRestore(entry.version)}
                  onExport={() => void exportVersion(entry.version)}
                  onDelete={() => void deleteScenario(entry.version)}
                />
              ))}
            </div>
          )}
        </section>

        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-700">History</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Saved automatically before each change, so any of them can be
              returned to.
            </p>
          </div>
          {history.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              No earlier versions yet — history starts at the first change.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {history.map((entry) => (
                <VersionRow
                  key={entry.version}
                  entry={entry}
                  isCurrent={entry.version === model.version}
                  onRestore={() => setPendingRestore(entry.version)}
                  onExport={() => void exportVersion(entry.version)}
                />
              ))}
            </div>
          )}
        </section>

        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">
            Reset
          </div>
          <div className="space-y-3 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-gray-700">
                Clear all overridden cells
                <div className="text-xs text-gray-500">
                  {overrideCount === 0
                    ? 'No cells are overridden.'
                    : `${overrideCount} cell${overrideCount === 1 ? '' : 's'} currently overridden.`}
                </div>
              </div>
              <button
                type="button"
                disabled={overrideCount === 0}
                onClick={() => void clearAllOverrides()}
                className="shrink-0 rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Clear
              </button>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
              <div className="text-sm text-gray-700">
                Clear all inputs
                <div className="text-xs text-gray-500">
                  Empties every figure, keeping the forecast period. Saved to
                  history first, so it can be undone.
                </div>
              </div>
              <button
                type="button"
                onClick={() => void resetInputs()}
                className="shrink-0 rounded border border-gray-300 px-3 py-1 text-sm text-red-700 hover:bg-red-50"
              >
                Reset inputs
              </button>
            </div>
          </div>
        </section>
      </div>

      {pendingRestore !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
            <h3 className="text-sm font-semibold text-gray-900">
              Restore version {pendingRestore}?
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              The model as it stands now is saved to history first, so you can
              come back to it.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingRestore(null)}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const version = pendingRestore;
                  setPendingRestore(null);
                  await restoreVersion(version);
                }}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white"
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
